import {FormEvent, useCallback, useMemo, useState} from "react";
import {styled} from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@memaro/components/Button.tsx";
import Form from "@memaro/components/Form.tsx";
import Alert from "@mui/material/Alert";
import {useAuth} from "../lib/auth.ts";
import {useFormInput} from "@memaro/forms";
import {
    EmptyUpdatePassword,
    InvalidPasswordException,
    NotAuthorizedException,
    PasswordsDoNotMatch,
    nameFromError,
} from "@memaro/auth";
import {useRouter} from "../hooks/useRouter";
import {useFeedback} from "@memaro/feedback";
import Menu from "../components/Menu.tsx";
import HomeButton from "../components/Menu/HomeButton.tsx";
import {resetPassword} from "../hoc/auth.tsx";

const StyledStack = styled(Stack)({
    width: 'fit-content',
    margin: '0 auto'
});

const StyledTextField = styled(TextField)({
    marginTop: 30,
    minWidth: 300,
    fontFamily: "inherit"
})

const ChangePasswordPage = resetPassword(() => {
    const {gotoHome} = useRouter();
    const {updatePassword, confirmSignIn, state} = useAuth()
    const {success} = useFeedback()

    const [oldPassword, setOldPassword] = useFormInput()
    const [newPassword, setNewPassword] = useFormInput()
    const [repeatNewPassword, setRepeatNewPassword] = useFormInput()
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>("")

    const handleRegister = useCallback(async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (newPassword != repeatNewPassword) {
            setError(PasswordsDoNotMatch)
            return
        }
        setLoading(true)
        try {
            if (state == "confirm_sign_in_with_new_password") {
                await confirmSignIn({
                    challengeResponse: newPassword,
                })
            } else {
                await updatePassword({
                    oldPassword,
                    newPassword
                });
            }
            success?.("Uw nieuwe wachtwoord is succesvol opgeslagen")
            gotoHome()
        } catch (e: unknown) {
            setError(nameFromError(e))
        } finally {
            setLoading(false)
        }
    }, [newPassword, repeatNewPassword, oldPassword, updatePassword, success, gotoHome])

    const errorText = useMemo(() => {
        switch (error) {
            case "":
                return ""
            case NotAuthorizedException:
                return "Oud wachtwoord is onjuist"
            case EmptyUpdatePassword:
                return "Oud wachtwoord is vereist"
            case PasswordsDoNotMatch:
                return "Opgegeven wachtwoorden komen niet overeen"
            case InvalidPasswordException:
                return "Wachtwoord voldoet niet aan alle eisen"
            default:
                console.error("Unknown error:", error)
                return "Onbekende fout opgetreden"
        }
    }, [error])

    const oldPasswordRequired = state != "confirm_sign_in_with_new_password"

    return (
        <>
            <Menu>
                <HomeButton/>
            </Menu>
            <StyledStack>
                <Form onSubmit={handleRegister}>
                    <h1>Wachtwoord wijzigen</h1>
                    {errorText ? <Alert severity="error">{errorText}</Alert> : null}
                    {oldPasswordRequired ? <StyledTextField type="password" label="Oud wachtwoord" autoComplete="password" value={oldPassword} onChange={setOldPassword}/> : null}
                    <StyledTextField type="password" label="Nieuw wachtwoord" autoComplete="new-password" value={newPassword} onChange={setNewPassword}/>
                    <StyledTextField type="password" label="Herhaal nieuw wachtwoord" autoComplete="new-password" value={repeatNewPassword} onChange={setRepeatNewPassword}/>
                    <Box sx={{display: "flex", flexDirection: "row-reverse"}}>
                        <Box>
                            <Button color="primary" type="submit" sx={{marginTop: "10px", marginLeft: "10px"}} loading={loading}>Opslaan</Button>
                        </Box>
                    </Box>
                </Form>
            </StyledStack>
        </>
    )
})

export default ChangePasswordPage
